import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { Button, Steps, Alert, Spin, message } from "antd";
import {
  CheckCircleOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  BankOutlined,
  CreditCardOutlined,
  SafetyOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../../context/AuthProvider";
import {
  createConnectAccount,
  createAccountLink,
  getConnectAccountStatus,
} from "../../lib/payments/stripe";
import { updateConnectAccountInSettings } from "../../lib/payments/connectStatus";
import { trackEvent } from "../../helpers/mixpanel";
import SettingsModel from "../../lib/firebase/settingsModel";

const { Step } = Steps;

const ConnectOnboarding = ({ onComplete, onCancel, inModal }) => {
  const { user } = useContext(AuthContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [accountId, setAccountId] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user already has a Connect account
    checkExistingAccount();
  }, []);

  const checkExistingAccount = async () => {
    try {
      // In a real implementation, this would check if the user already has a Connect account
      // For now, we'll assume they don't have one
      setCurrentStep(0);
    } catch (error) {
      console.error("Error checking existing account:", error);
    }
  };

  const startOnboarding = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is authenticated
      if (!user) {
        throw new Error("User not authenticated. Please log in to continue.");
      }

      if (!user.email) {
        throw new Error(
          "User email not available. Please ensure your account has a valid email address."
        );
      }

      // Check if user already has an existing Stripe Connect account
      let existingAccountId = null;
      try {
        const userSettings = await SettingsModel.getByUserId(user.uid);
        existingAccountId = userSettings?.paymentSetup?.stripeAccountId || null;
      } catch (settingsError) {
        console.warn("Could not check for existing account:", settingsError);
        // Continue with creating new account if check fails
      }

      let accountIdToUse = null;

      if (existingAccountId) {
        // Resume onboarding for existing account
        console.log(
          "Resuming onboarding for existing account:",
          existingAccountId
        );
        accountIdToUse = existingAccountId;
        setAccountId(existingAccountId);

        trackEvent("stripe_connect_onboarding_resumed", {
          user_id: user.uid,
          account_id: existingAccountId,
        });
      } else {
        // Create new Stripe Connect account
        console.log("Creating new Stripe Connect account");
        const accountResponse = await createConnectAccount(
          user.email,
          user.uid,
          "US"
        );
        accountIdToUse = accountResponse.accountId;
        setAccountId(accountResponse.accountId);

        // Store the account ID in Firebase settings immediately
        try {
          await updateConnectAccountInSettings(
            user.uid,
            accountResponse.accountId,
            {
              details_submitted: false,
              charges_enabled: false,
              payouts_enabled: false,
            }
          );
          console.log("Connect account ID stored in Firebase settings");
        } catch (updateError) {
          console.error(
            "Error storing Connect account ID in Firebase:",
            updateError
          );
          // Don't fail the onboarding if Firebase update fails
        }

        trackEvent("stripe_connect_onboarding_started", {
          user_id: user.uid,
          account_id: accountResponse.accountId,
        });
      }

      // Create account link for onboarding (works for both new and existing accounts)
      const linkResponse = await createAccountLink(
        accountIdToUse,
        `${window.location.origin}/payment-setup?refresh=true`,
        `${window.location.origin}/payment-setup?success=true&account=${accountIdToUse}`
      );

      // Redirect to Stripe onboarding
      window.location.href = linkResponse.url;
    } catch (error) {
      console.error("Onboarding error:", error);

      // Provide more helpful error messages
      let errorMessage = error.message || "Failed to start onboarding process";

      if (errorMessage.includes("User not authenticated")) {
        errorMessage =
          "Please log in to your account to set up payment processing.";
      } else if (errorMessage.includes("User email not available")) {
        errorMessage =
          "Your account needs a valid email address to set up payment processing.";
      } else if (
        errorMessage.includes("Backend API endpoint not available") ||
        errorMessage.includes("Payment processing is not yet configured")
      ) {
        errorMessage =
          "Payment processing backend is not yet configured. Please contact your developer to implement the required API endpoints.";
      } else if (errorMessage.includes("Failed to fetch")) {
        errorMessage =
          "Unable to connect to the payment service. Please check your internet connection and try again.";
      }

      setError(errorMessage);

      trackEvent("stripe_connect_onboarding_failed", {
        user_id: user.uid,
        error: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const checkAccountStatus = async () => {
    if (!accountId) return;

    try {
      const status = await getConnectAccountStatus(accountId);
      setAccountStatus(status);

      if (status.details_submitted && status.charges_enabled) {
        setCurrentStep(3); // Complete
        message.success("Payment processing setup complete!");

        trackEvent("stripe_connect_onboarding_completed", {
          user_id: user.uid,
          account_id: accountId,
        });

        if (onComplete) {
          onComplete(status);
        }
      } else {
        setCurrentStep(2); // In progress
      }
    } catch (error) {
      console.error("Error checking account status:", error);
      setError("Failed to check account status");
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Not Started
        return (
          <StepContent>
            <StepTitle>Set Up Payment Processing</StepTitle>
            <StepDescription>
              To receive rent payments from your tenants, you need to complete
              Stripe Connect onboarding. This allows us to securely process
              payments and transfer funds to your bank account.
            </StepDescription>

            <FeatureList>
              <FeatureItem>
                <SafetyOutlined />
                <span>Secure payment processing</span>
              </FeatureItem>
              <FeatureItem>
                <BankOutlined />
                <span>Direct bank transfers</span>
              </FeatureItem>
              <FeatureItem>
                <DollarOutlined />
                <span>Automatic reconciliation</span>
              </FeatureItem>
              <FeatureItem>
                <CreditCardOutlined />
                <span>PCI compliant</span>
              </FeatureItem>
            </FeatureList>

            {error && (
              <Alert
                message="Error"
                description={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{ marginBottom: "1rem" }}
              />
            )}

            <ActionButton
              type="primary"
              size="large"
              onClick={startOnboarding}
              loading={loading}
            >
              Start Onboarding
            </ActionButton>
          </StepContent>
        );

      case 1: // In Progress
        return (
          <StepContent>
            <StepTitle>Completing Setup</StepTitle>
            <StepDescription>
              Please complete the Stripe Connect onboarding process in the new
              window. This includes providing your business information and bank
              account details.
            </StepDescription>

            <ProcessingContent>
              <Spin size="large" />
              <ProcessingText>Setting up your account...</ProcessingText>
              <ProcessingDescription>
                Complete the onboarding process to enable payment processing
              </ProcessingDescription>
            </ProcessingContent>

            <ActionButton
              type="default"
              size="large"
              onClick={checkAccountStatus}
              loading={loading}
            >
              Check Status
            </ActionButton>
          </StepContent>
        );

      case 2: // Verification Required
        return (
          <StepContent>
            <StepTitle>Verification Required</StepTitle>
            <StepDescription>
              Your account is being verified by Stripe. This usually takes a few
              minutes to a few hours.
            </StepDescription>

            <Alert
              message="Account Under Review"
              description="Stripe is reviewing your account information. You'll receive an email when verification is complete."
              type="info"
              showIcon
              icon={<ExclamationCircleOutlined />}
              style={{ marginBottom: "2rem" }}
            />

            <ActionButton
              type="default"
              size="large"
              onClick={checkAccountStatus}
              loading={loading}
            >
              Check Status Again
            </ActionButton>
          </StepContent>
        );

      case 3: // Complete
        return (
          <StepContent>
            <SuccessContent>
              <CheckCircleOutlined className="success-icon" />
              <SuccessTitle>Setup Complete!</SuccessTitle>
              <SuccessDescription>
                Your payment processing is now active. You can start receiving
                rent payments from your tenants.
              </SuccessDescription>

              <FeatureList>
                <FeatureItem>
                  <CheckCircleOutlined />
                  <span>Payment processing enabled</span>
                </FeatureItem>
                <FeatureItem>
                  <CheckCircleOutlined />
                  <span>Bank account connected</span>
                </FeatureItem>
                <FeatureItem>
                  <CheckCircleOutlined />
                  <span>Ready to receive payments</span>
                </FeatureItem>
              </FeatureList>

              <ActionButton
                type="primary"
                size="large"
                onClick={() => onComplete && onComplete(accountStatus)}
              >
                Continue to Dashboard
              </ActionButton>
            </SuccessContent>
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <OnboardingShell $inModal={inModal}>
      <Steps current={currentStep} style={{ marginBottom: "2rem" }}>
        <Step title="Setup" description="Start onboarding" />
        <Step title="Information" description="Provide details" />
        <Step title="Verification" description="Account review" />
        <Step title="Complete" description="Ready to go" />
      </Steps>

      {renderStepContent()}
    </OnboardingShell>
  );
};

// Styled Components
const OnboardingShell = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 auto;

  ${(p) =>
    p.$inModal
      ? `
    border: none;
    border-radius: 0;
    background: transparent;
    padding: 0;
    box-shadow: none;
  `
      : `
    border-radius: 18px;
    border: 1px solid rgba(44, 95, 45, 0.18);
    background: #fdfdfb;
    padding: clamp(24px, 4vw, 36px);
    box-shadow: 0 16px 44px rgba(33, 56, 41, 0.08);
  `}

  .ant-steps {
    margin-bottom: 32px !important;

    .ant-steps-item-title {
      font-family:
        "Inter",
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
      font-weight: 600;
      color: #2c5f2d;
      font-size: 15px;
    }

    .ant-steps-item-description {
      font-family:
        "Inter",
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;
      color: #4a7c59;
      font-size: 13px;
    }

    .ant-steps-item-icon {
      border-width: 2px;
      width: 34px;
      height: 34px;
    }

    .ant-steps-item-process .ant-steps-item-icon,
    .ant-steps-item-finish .ant-steps-item-icon {
      background-color: #2c5f2d !important;
      border-color: #2c5f2d !important;
      color: #fff !important;
    }

    .ant-steps-item-wait .ant-steps-item-icon {
      background-color: #f4f6f0 !important;
      border-color: rgba(44, 95, 45, 0.25) !important;
      color: #8da38d !important;
    }

    .ant-steps-item-process .ant-steps-item-title,
    .ant-steps-item-finish .ant-steps-item-title {
      color: #2c5f2d !important;
    }
  }

  @media (max-width: 768px) {
    ${(p) =>
      p.$inModal
        ? ""
        : `
      padding: 20px;
      border-radius: 14px;
    `}
  }
`;

const StepContent = styled.div`
  text-align: center;
  max-width: 560px;
  margin: 0 auto;
`;

const StepTitle = styled.h2`
  color: #1f331f;
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 12px;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const StepDescription = styled.p`
  color: #516552;
  font-size: 17px;
  line-height: 1.55;
  margin: 0 auto 28px;
  max-width: 520px;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 24px;
  }
`;

const FeatureList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin: 20px 0 28px;
  text-align: left;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin: 20px 0;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  padding: 18px;
  background: #fff;
  border: 1px solid rgba(44, 95, 45, 0.2);
  border-radius: 12px;
  gap: 12px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);

  .anticon {
    font-size: 22px;
    color: #2c5f2d;
  }

  span {
    color: #2c5f2d;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
    font-weight: 600;
    font-size: 15px;
  }
`;

const ActionButton = styled(Button)`
  min-width: 200px;
  height: 46px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 999px;
  padding: 0 28px;
  background: #2c5f2d;
  border: none;
  box-shadow: 0 6px 18px rgba(44, 95, 45, 0.25);

  @media (max-width: 768px) {
    min-width: 100%;
    height: 44px;
  }
`;

const ProcessingContent = styled.div`
  padding: 40px 20px;
  text-align: center;
`;

const ProcessingText = styled.div`
  margin-top: 24px;
  font-size: 20px;
  font-weight: 600;
  color: #2c5f2d;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
`;

const ProcessingDescription = styled.div`
  margin-top: 12px;
  color: #4a7c59;
  font-size: 16px;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
`;

const SuccessContent = styled.div`
  padding: 20px;
  text-align: center;
`;

const SuccessTitle = styled.h2`
  color: #2c5f2d;
  font-size: 28px;
  font-weight: 700;
  margin: 24px 0 16px 0;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const SuccessDescription = styled.p`
  color: #4a7c59;
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 32px;
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 24px;
  }
`;

export default ConnectOnboarding;
